import React from 'react';
import { DatePicker, Form } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { DateFieldConfig, ValueChangeEvent } from '../types';
import { getDateFieldOptions } from '../types';

interface DateFieldProps {
  config: DateFieldConfig;
  value?: string;
  onChange: (event: ValueChangeEvent) => void;
  pageId: string;
  error?: string[];
}

export const DateField: React.FC<DateFieldProps> = ({
  config,
  value,
  onChange,
  pageId,
  error
}) => {
  // Extract date field specific options
  const dateOptions = getDateFieldOptions(config);
  const handleChange = (date: Dayjs, dateString: string | string[]) => {
    onChange({
      componentId: config.id,
      value: Array.isArray(dateString) ? dateString[0] : dateString,
      pageId
    });
  };

  const validateRules = () => {
    const rules: any[] = [];
    
    if (config.required) {
      rules.push({
        required: true,
        message: `${config.label} is required`
      });
    }
    
    // Add custom validation rules
    config.validation?.forEach(rule => {
      switch (rule.type) {
        case 'custom':
          rules.push({
            validator: rule.value,
            message: rule.message
          });
          break;
        // Add more validation types as needed
      }
    });
    
    return rules;
  };

  const getDateValue = (): Dayjs | undefined => {
    const dateValue = value || config.defaultValue;
    return dateValue ? dayjs(dateValue) : undefined;
  };

  const getMinDate = (): Dayjs | undefined => {
    return dateOptions.minDate ? dayjs(dateOptions.minDate) : undefined;
  };

  const getMaxDate = (): Dayjs | undefined => {
    return dateOptions.maxDate ? dayjs(dateOptions.maxDate) : undefined;
  };

  const disabledDate = (current: Dayjs): boolean => {
    const minDate = getMinDate();
    const maxDate = getMaxDate();
    
    if (minDate && current.isBefore(minDate, 'day')) {
      return true;
    }
    
    if (maxDate && current.isAfter(maxDate, 'day')) {
      return true;
    }
    
    return false;
  };

  const datePickerProps = {
    value: getDateValue(),
    onChange: handleChange,
    disabled: config.disabled,
    format: dateOptions.format || (dateOptions.showTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'),
    showTime: dateOptions.showTime || false,
    disabledDate: (dateOptions.minDate || dateOptions.maxDate) ? disabledDate : undefined,
    style: { width: '100%' },
    placeholder: `Select ${config.label.toLowerCase()}`
  };

  const formItemProps = {
    label: config.label,
    required: config.required,
    validateStatus: error && error.length > 0 ? 'error' as const : undefined,
    help: error && error.length > 0 ? error[0] : config.description,
    rules: validateRules(),
    labelCol: { span: 24 },
    wrapperCol: { span: 24 }
  };

  return (
    <Form.Item {...formItemProps}>
      <DatePicker {...datePickerProps} />
    </Form.Item>
  );
}; 